import type { BlockTitleImageAndContent } from 'types/block-title-image-and-content';

import React, { useState, useMemo, useEffect } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { usePostHog } from 'posthog-js/react';

import { Wrap, Button } from '@leafwell/components';

import { getRandomInt } from 'lib/utils/generateRandomInt';
import { useMatchMedia } from 'hooks/useMatchMedia';

import HeadingBody from './heading-body';
import WysiwygContent from './wysiwyg-content';
import Figure from './figure';
import { useEventsTracker } from 'contexts/eventsTracker';
import VideoYoutube from './video-youtube';
import { toQueryString } from 'lib/utils/toQueryString';

const ContentItem: React.FC<
  BlockTitleImageAndContent['content'][0] & { isMobileCollapsible: boolean }
> = ({
  contentTitle,
  contentDescription,
  contentIcon,
  isMobileCollapsible,
}) => {
  const [isMobile] = useMatchMedia(['(max-width: 768px)'], [true]);
  const [isOpen, setIsOpen] = useState(false);

  const collapse = useMemo(() => {
    return isMobile && isMobileCollapsible && contentDescription;
  }, [isMobile, isMobileCollapsible, contentDescription]);

  const toggleCollapsible = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div>
      <div
        className={[
          'flex gap-x-5 items-center',
          collapse ? 'cursor-pointer' : '',
        ].join(' ')}
        onClick={e => {
          if (collapse) {
            e.preventDefault();
            toggleCollapsible();
          }
        }}
      >
        {contentIcon && (
          <Figure
            {...contentIcon}
            className="inline-flex items-center justify-center w-12 h-12 lg:w-16 lg:h-16"
          />
        )}
        <HeadingBody
          level="5"
          tagName="h5"
          title={contentTitle}
          className="font-semibold"
        />
        <Image
          src="/chevron.svg"
          width={24}
          height={24}
          className={[
            collapse ? (isOpen ? 'ml-auto rotate-180' : 'ml-auto') : 'hidden',
          ].join(' ')}
          alt=""
        />
      </div>
      {contentDescription && (
        <WysiwygContent
          content={contentDescription}
          className={[
            '!text-gray-700',
            collapse
              ? isOpen
                ? 'mt-4'
                : 'max-h-0 overflow-hidden transition-max-height duration-200 ease-out'
              : 'mt-4',
          ].join(' ')}
        />
      )}
    </div>
  );
};

const TitleImageAndContent: React.FC<BlockTitleImageAndContent> = ({
  sectionId,
  instanceId,
  title,
  description,
  displayContent,
  image,
  youtubeVideo,
  content,
  buttons,
}) => {
  const isMobileCollapsible = content?.some(
    ({ contentDescription }) => contentDescription?.length > 0,
  );

  const { trackEvent } = useEventsTracker();
  const { asPath, query } = useRouter();
  const posthog = usePostHog();
  const sessionId = posthog.get_session_id();
  const distinctId = posthog.get_distinct_id();
  const queryParams = toQueryString(query);
  const [posthogUniqueIds, setPosthogUniqueIds] = useState({
    distinctId,
    sessionId,
    queryParams,
  });

  const product: 'membership' | 'telepharmacist' | null = asPath.includes(
    '/membership',
  )
    ? 'membership'
    : asPath.includes('/cannabis-care')
    ? 'telepharmacist'
    : null;

  useEffect(() => {
    if (product) {
      setPosthogUniqueIds({
        distinctId,
        sessionId,
        queryParams,
      });
    }
  }, [sessionId, distinctId, product, queryParams]);

  return (
    <Wrap
      className="grid lg:grid-cols-2 gap-x-24"
      id={sectionId}
      itemID={`TitleImageAndContent-${instanceId}`}
    >
      <div className="grid gap-y-14">
        <div className="grid gap-y-6">
          {title && (
            <HeadingBody
              level="2"
              tagName="h2"
              title={title}
              className="font-semibold"
            />
          )}
          {description && (
            <HeadingBody
              level="6"
              tagName="p"
              title={description}
              className="text-gray-700"
            />
          )}
        </div>
        {displayContent === 'image' && image && <Figure {...image} />}
        {displayContent === 'video' &&
          youtubeVideo &&
          youtubeVideo.youtubeVideoId && (
            <VideoYoutube
              title=""
              content=""
              youtubeVideos={Array({
                youtubeVideoId: youtubeVideo.youtubeVideoId,
                image: youtubeVideo.videoThumbnail,
                playButtonStyle: 'secondary',
              })}
              videoSize="large"
              variant="none"
              sectionId=""
              removePadding={true}
            />
          )}
      </div>
      <div className="grid gap-y-11">
        {content?.map(({ contentTitle, contentDescription, contentIcon }) => {
          const idx = getRandomInt(1, 1000000);
          return (
            <ContentItem
              key={`${idx}-title-image-and-content`}
              contentTitle={contentTitle}
              contentDescription={contentDescription}
              contentIcon={contentIcon}
              isMobileCollapsible={isMobileCollapsible}
            />
          );
        })}
        {buttons ? (
          <div className="flex flex-col lg:flex-row gap-4">
            {buttons.map(({ button, buttonVariant, isDownloadButton }) => {
              const idx = getRandomInt(1, 1000000);
              return (
                <Button
                  key={`${idx}-title-image-and-content-button`}
                  href={
                    isDownloadButton
                      ? button.url
                      : product
                      ? button?.url?.includes('?')
                        ? `${button.url}&session_id=${
                            posthogUniqueIds?.sessionId
                          }&distinct_id=${posthogUniqueIds?.distinctId}${
                            queryParams
                              ? `&${posthogUniqueIds?.queryParams}`
                              : ''
                          }`
                        : `${button.url}?session_id=${
                            posthogUniqueIds?.sessionId
                          }&distinct_id=${posthogUniqueIds?.distinctId}${
                            queryParams
                              ? `&${posthogUniqueIds?.queryParams}`
                              : ''
                          }`
                      : button.url
                  }
                  label={button.title}
                  rounded={true}
                  filled={buttonVariant === 'primary' ? true : false}
                  variant="secondary"
                  target={button.target}
                  className="w-fit h-fit"
                  icon={isDownloadButton ? 'download' : 'arrow'}
                  onClick={() => {
                    trackEvent({
                      event: 'select_content',
                      content_type: button.title,
                      item_id: `TitleImageAndContent-${instanceId}`,
                      action: isDownloadButton
                        ? 'TitleImageAndContent-download-file'
                        : 'TitleImageAndContent-click-cta',
                    });
                    if (product && !isDownloadButton) {
                      trackEvent({
                        event: 'generate_lead',
                        item_id: `TitleImageAndContent-${instanceId}`,
                        service: product,
                        page_referrer: document.referrer,
                      });
                    }
                  }}
                />
              );
            })}
          </div>
        ) : null}
      </div>
    </Wrap>
  );
};

export default TitleImageAndContent;
